import { Button } from "@mui/material"
import { ClimateZone, Comune, HeatPumpProduct, ProductTypeId, SolarProduct, SolarService, TerminalType, WaterHeaterProduct } from "../types/types"
import "./Results.css"
import dataComuni from "../data/comuni-zona-climatiche.json"
import dataProductsPdc from "../data/products_pdc.json"
import dataScaldacqua from "../data/products_scaldacqua.json"
import dataSolar from "../data/solar_products.json"
import dataSolarServices from "../data/solar_services.json"
import { HEAT_PUMP_PRODUCT_TYPES, TERMINAL_LABEL_BY_ID } from "../constants/productTypes"
import ResultRow from "./ResultRow";

interface Props {
    tipo: ProductTypeId;
    modello: string;
    comune: string | null;
    terminale: TerminalType | null;
    service: string | null;
    panels: string | null;
    setShow: () => void;
    setComune: (data: string | null) => void;
    setTerminal: (data: TerminalType | null) => void;
    setProduct: (data: string | null) => void;
    setType: (data: ProductTypeId | null) => void;
}

export default function Results({ tipo, comune, terminale, modello, service, panels, setShow, setComune, setTerminal, setProduct, setType }: Props) {
    const comuni: Comune[] = dataComuni;
    const productsPdc: HeatPumpProduct[] = dataProductsPdc;
    const productsScaldacqua: WaterHeaterProduct[] = dataScaldacqua;
    const productsSolar: SolarProduct[] = dataSolar;

    const selectedComune = comuni.find(i => i.nome === comune);
    const selectedProductPdc: HeatPumpProduct | undefined = productsPdc.find(i => i.modello === modello);
    const selectedScaldacqua: WaterHeaterProduct | undefined = productsScaldacqua.find(i => i.modello === modello);
    const selectedSolarProduct: SolarProduct | undefined = productsSolar.find(i => i.modello === modello);
    const selectedService: SolarService | undefined = dataSolarServices.find(i => i.nome === service);
    const terminalKey = terminale;
    const selectedHeatPump = selectedProductPdc;
    const heatPumpMultiplier = tipo === ProductTypeId.IBRIDO_FACTORY ? 1.25 : tipo === ProductTypeId.IBRIDO_BIVALENTE_GT35 ? 1.1 : 1;
    const climateZone = selectedComune?.zona as ClimateZone | undefined;
    const baseIncentive = selectedHeatPump && climateZone && terminalKey ? selectedHeatPump.incentivi[terminalKey][climateZone] : null;
    const totalHeatPumpIncentive = baseIncentive ? baseIncentive * heatPumpMultiplier : null;
    const panelData = selectedSolarProduct && selectedService && panels
        ? selectedSolarProduct.servizio[selectedService.id]?.pannelli[panels]
        : null;

    const modelloLabel = (() => {
        if (tipo === ProductTypeId.IBRIDO_FACTORY) {
            return `Sistema ibrido Factory Made con pompa di calore ${modello}`;
        }
        if (tipo === ProductTypeId.IBRIDO_BIVALENTE_LT35) {
            return `Sistema ibrido bivalente o pompa di calore add-on con ${modello} e caldaia < 35 kW`;
        }
        if (tipo === ProductTypeId.IBRIDO_BIVALENTE_GT35) {
            return `Sistema ibrido bivalente o pompa di calore add-on con ${modello} e caldaia > 35 kW`;
        }
        return modello;
    })();

    const extraText = (() => {
        if (tipo === ProductTypeId.IBRIDO_FACTORY) {
            return "Nel sistema ibrido Factory Made il rapporto tra la potenza nominale in riscaldamento della pompa di calore e la potenza termica utile della caldaia a condensazione deve essere minore o uguale a 0,5. Il valore dell'incentivo è maggiorato del 25% rispetto a quello della pompa di calore che costituisce il sistema.";
        }
        if (tipo === ProductTypeId.IBRIDO_BIVALENTE_LT35) {
            return "Il sistema ibrido Bivalente è costituito da un gruppo primario a pompa di calore, abbinato ad un gruppo secondario costituito da una caldaia a condensazione a gas, non assemblati in fabbrica, ma in campo. La pompa di calore add-on è un sistema costituito da un generatore a pompa di calore installata ad integrazione di una caldaia a condensazione alimentata a gas preesistente non più vecchia di 5 anni, e combinato con essa al fine di costituire un sistema bivalente. Il valore dell'incentivo è uguale a quello della pompa di calore che costituisce il sistema quando la potenza termica della caldaia è < 35 kW.";
        }
        if (tipo === ProductTypeId.IBRIDO_BIVALENTE_GT35) {
            return "Il sistema ibrido Bivalente è costituito da un gruppo primario a pompa di calore, abbinato ad un gruppo secondario costituito da una caldaia a condensazione a gas, non assemblati in fabbrica, ma in campo. La pompa di calore add-on è un sistema costituito da un generatore a pompa di calore installata ad integrazione di una caldaia a condensazione alimentata a gas preesistente non più vecchia di 5 anni, e combinato con essa al fine di costituire un sistema bivalente. Il valore dell'incentivo è uguale a quello della pompa di calore che costituisce il sistema maggiorato del 10% quando la potenza termica della caldaia è > 35 kW.";
        }
        return null;
    })();

    const capacity = selectedScaldacqua?.modello.match(/(\d+)\s*lt/i)?.[1];

    return (
        <div className="flex justify-center border md:border-solid border-[#D8D8D8] rounded-2xl md:mx-5">
            <div className="flex flex-col items-center md:py-10 px-5 max-w-4xl">
                <div className="w-full md:text-center">
                    <h4 className='uppercase font-black'>Il risultato del conto termico</h4>
                </div>
                <div className='flex flex-col gap-4 pt-8 items-center'>
                    <ResultRow label="Il tuo modello" value={modelloLabel} labelClassName="text-nowrap" />
                    {HEAT_PUMP_PRODUCT_TYPES.includes(tipo) && selectedHeatPump && terminalKey &&
                        <>
                            <ResultRow label="Terminali" value={TERMINAL_LABEL_BY_ID[terminalKey]} />
                            {selectedComune &&
                                <>
                                    <ResultRow label="Zona Climatica" value={selectedComune.zona} />
                                </>
                            }
                            <ResultRow label={`Prated (${terminalKey} temperatura)`} value={selectedHeatPump.pn[terminalKey].toLocaleString("it-IT")} />
                            <ResultRow label={`SCOP (${terminalKey} temperatura)`} value={selectedHeatPump.scop[terminalKey].toLocaleString("it-IT")} />
                            <ResultRow
                                label={`ηs efficienza energetica stagionale (${terminalKey} temperatura)`}
                                value={Math.round(selectedHeatPump.ηs[terminalKey])}
                            />
                            {totalHeatPumpIncentive &&
                                <ResultRow
                                    label="Incentivo totale [€]*"
                                    value={`€ ${totalHeatPumpIncentive.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                    rowClassName="bg-gray-light"
                                />
                            }
                        </>
                    }
                    {tipo === ProductTypeId.SCALDACQUA && selectedScaldacqua &&
                        <>
                            <ResultRow label="Classe energetica" value={selectedScaldacqua.classe} />
                            <ResultRow label="Capacità dell'accumulo" value={capacity ? `${capacity} lt` : "-"} />
                            <ResultRow
                                label="Incentivo totale [€]*"
                                value={selectedScaldacqua.incentivoSingolo}
                                rowClassName="bg-gray-light"
                            />
                        </>
                    }
                    {
                        tipo === ProductTypeId.SOLARE && selectedSolarProduct && selectedService && panels && panelData &&
                        <>
                            <ResultRow label="Servizio" value={selectedService.nome} />
                            <ResultRow label="Numero pannelli" value={panels} />
                            <ResultRow
                                label="Qu - Energia termica prodotta per singolo modulo"
                                value={selectedSolarProduct.qu.toLocaleString("it-IT")}
                            />
                            <ResultRow
                                label="Qcol - Energia termica prodotta in un anno dal singolo modulo"
                                value={selectedSolarProduct.qcol.toLocaleString("it-IT")}
                            />
                            <ResultRow label="Ag - Area lorda del pannello solare" value={selectedSolarProduct.ag.toLocaleString("it-IT")} />
                            <ResultRow label="Sl - Superfice solare lorda" value={panelData.sl.toLocaleString("it-IT")} />
                            <ResultRow
                                label="Incentivo totale [€]*"
                                value={`€ ${panelData.incentivo.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                rowClassName="bg-gray-light"
                            />
                        </>
                    }
                    <div className='flex flex-col gap-8'>
                        {extraText && <p className='w-full text-sm'>{extraText}</p>}
                        <p className='w-full text-sm'>*Incentivo fino a 15.000€ erogato in unica rata.</p>
                        <p className='font-black leading-6'>
                            COSMOGAS fornisce solo una stima dell'incentivo ottenibile con il Conto Termico. È responsabilità del richiedente verificare il soddisfacimento di tutte le condizioni necessarie per aderire al Conto Termico. I valori indicati possono subire variazioni senza preavviso, pertanto questo documento non può essere considerato un contratto vincolante nei confronti di terzi.
                        </p>
                        <Button
                            variant='contained'
                            onClick={e => {
                                setShow()
                                setComune(null)
                                setTerminal(null)
                                setProduct(null)
                                setType(null)
                            }}
                            className='mx-auto bg-white hover:bg-yellow text-blue border-blue rounded-none border border-solid text-xl p-4 font-black w-full md:max-w-[320px] normal-case'
                            fullWidth
                        >
                            Modifica parametri iniziali
                        </Button>
                    </div>
                </div>
            </div>
        </div>)
}
